import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useProducts } from '@/hooks/useProducts';
import { NutritionSummary } from './NutritionSummary';
import type {
  Dish,
  CreateDishRequest,
  GoalOverride,
  NutritionValues,
} from '@/types/api';

interface IngredientDraft {
  productId: string;
  quantityG: string;
  removable: boolean;
}

interface DishFormProps {
  initial?: Dish;
  submitting: boolean;
  onSubmit: (data: CreateDishRequest) => void;
  onCancel: () => void;
  submitLabel: string;
}

const EMPTY_DRAFT: IngredientDraft = {
  productId: '',
  quantityG: '100',
  removable: false,
};

const GOAL_OPTIONS: Array<{ value: '' | GoalOverride; labelKey: string }> = [
  { value: '', labelKey: 'dishes.goalAuto' },
  { value: 'weight_loss', labelKey: 'goal.weight_loss' },
  { value: 'maintenance', labelKey: 'goal.maintenance' },
  { value: 'muscle_gain', labelKey: 'goal.muscle_gain' },
  { value: 'none', labelKey: 'dishes.goalNone' },
];

export function DishForm({
  initial,
  submitting,
  onSubmit,
  onCancel,
  submitLabel,
}: DishFormProps) {
  const { t } = useTranslation();
  const [name, setName] = useState(initial?.name ?? '');
  const [servings, setServings] = useState(String(initial?.servings ?? 1));
  const [goalOverride, setGoalOverride] = useState<'' | GoalOverride>(
    initial?.goalOverride ?? '',
  );
  const [productSearch, setProductSearch] = useState('');
  const [ingredients, setIngredients] = useState<IngredientDraft[]>(
    initial
      ? initial.ingredients.map((i) => ({
          productId: String(i.productId),
          quantityG: String(i.quantityG),
          removable: i.removable,
        }))
      : [{ ...EMPTY_DRAFT }],
  );

  const { data: productsData } = useProducts({ limit: 100, query: productSearch });
  const products = useMemo(
    () => productsData?.products ?? [],
    [productsData],
  );

  const preview = useMemo<NutritionValues>(() => {
    const total: NutritionValues = {
      energyKcal: 0,
      proteins: 0,
      carbohydrates: 0,
      fat: 0,
      fiber: 0,
    };
    for (const draft of ingredients) {
      const product = products.find((p) => String(p.id) === draft.productId);
      if (!product) continue;
      const qty = parseFloat(draft.quantityG) || 0;
      const factor = qty / 100;
      total.energyKcal += (product.energyKcal100g ?? 0) * factor;
      total.proteins += (product.proteins100g ?? 0) * factor;
      total.carbohydrates += (product.carbohydrates100g ?? 0) * factor;
      total.fat += (product.fat100g ?? 0) * factor;
      total.fiber += (product.fiber100g ?? 0) * factor;
    }
    const divisor = parseFloat(servings) > 0 ? parseFloat(servings) : 1;
    return {
      energyKcal: total.energyKcal / divisor,
      proteins: total.proteins / divisor,
      carbohydrates: total.carbohydrates / divisor,
      fat: total.fat / divisor,
      fiber: total.fiber / divisor,
    };
  }, [ingredients, products, servings]);

  const updateIngredient = (index: number, patch: Partial<IngredientDraft>) => {
    setIngredients((prev) =>
      prev.map((item, i) => (i === index ? { ...item, ...patch } : item)),
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validIngredients = ingredients
      .filter((i) => i.productId !== '')
      .map((i) => ({
        productId: Number(i.productId),
        quantityG: parseFloat(i.quantityG) || 0,
        removable: i.removable,
      }))
      .filter((i) => i.quantityG > 0);

    if (!name.trim() || validIngredients.length === 0) {
      return;
    }

    onSubmit({
      name: name.trim(),
      servings: parseInt(servings, 10) || 1,
      goalOverride: goalOverride === '' ? null : goalOverride,
      ingredients: validIngredients,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('dishes.name')}
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            placeholder={t('dishes.namePlaceholder')}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('dishes.servings')}
          </label>
          <input
            type="number"
            min={1}
            value={servings}
            onChange={(e) => setServings(e.target.value)}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('dishes.goalOverride')}
        </label>
        <select
          value={goalOverride}
          onChange={(e) => setGoalOverride(e.target.value as '' | GoalOverride)}
          className="block w-full sm:w-72 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
        >
          {GOAL_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {t(option.labelKey)}
            </option>
          ))}
        </select>
        <p className="mt-1 text-xs text-gray-500">
          {t('dishes.goalOverrideHelp')}
        </p>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-900">
            {t('dishes.ingredients')}
          </h3>
          <button
            type="button"
            onClick={() => setIngredients((prev) => [...prev, { ...EMPTY_DRAFT }])}
            className="text-sm font-medium text-indigo-600 hover:text-indigo-800"
          >
            + {t('dishes.addIngredient')}
          </button>
        </div>

        <input
          type="text"
          value={productSearch}
          onChange={(e) => setProductSearch(e.target.value)}
          placeholder={t('products.searchPlaceholder')}
          className="block w-full mb-3 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
        />

        <div className="space-y-2">
          {ingredients.map((ingredient, index) => (
            <div
              key={index}
              className="flex flex-wrap items-center gap-2 p-2 bg-gray-50 rounded-lg"
            >
              <select
                value={ingredient.productId}
                onChange={(e) =>
                  updateIngredient(index, { productId: e.target.value })
                }
                className="flex-1 min-w-[12rem] rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
              >
                <option value="">{t('dishes.selectProduct')}</option>
                {products.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name}
                    {product.brand ? ` — ${product.brand}` : ''}
                  </option>
                ))}
              </select>

              <input
                type="number"
                min={1}
                value={ingredient.quantityG}
                onChange={(e) =>
                  updateIngredient(index, { quantityG: e.target.value })
                }
                className="w-24 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                placeholder="g"
              />

              <label className="inline-flex items-center gap-1 text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={ingredient.removable}
                  onChange={(e) =>
                    updateIngredient(index, { removable: e.target.checked })
                  }
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                {t('dishes.removable')}
              </label>

              {ingredients.length > 1 && (
                <button
                  type="button"
                  onClick={() =>
                    setIngredients((prev) => prev.filter((_, i) => i !== index))
                  }
                  className="text-gray-400 hover:text-red-500"
                  aria-label={t('common.remove')}
                >
                  ✕
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <NutritionSummary
          nutrition={preview}
          title={t('dishes.perServingPreview')}
        />
      </div>

      <div className="flex gap-3">
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
        >
          {submitting ? t('app.saving') : submitLabel}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          {t('app.cancel')}
        </button>
      </div>
    </form>
  );
}
